import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

interface PendingClientEstimate {
  id: number;
  case: {
    id: number;
    title: string;
    subtitle: string;
    project: {
      id: number;
      name: string;
    };
  };
  client_firm: {
    id: number;
    name: string;
  };
  created_by_user: {
    id: number;
    name: string;
  };
  billing_type: string;
  rate: number;
  quantity: number;
  appendix_c_rate?: number | null;
  appendix_c_quantity?: number;
  total_amount: number;
  description: string;
  status: string;
  created_at: string;
}

const PendingClientEstimates: React.FC = () => {
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState<PendingClientEstimate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectingEstimate, setRejectingEstimate] = useState<PendingClientEstimate | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadEstimates();
  }, []);

  const loadEstimates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/client-estimates/pending');
      if (response.data.success) {
        setEstimates(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load pending estimates');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (estimateId: number) => {
    if (!window.confirm('Are you sure you want to approve this estimate?')) {
      return;
    }

    try {
      setSubmitting(true);
      const response = await api.put(`/client-estimates/${estimateId}/status`, {
        action: 'approve'
      });
      
      if (response.data.success) {
        setSuccess('Estimate approved successfully');
        loadEstimates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to approve estimate');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectClick = (estimate: PendingClientEstimate) => {
    setRejectingEstimate(estimate);
    setRejectionReason('');
    setShowRejectDialog(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectingEstimate || !rejectionReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }

    try {
      setSubmitting(true);
      const response = await api.put(`/client-estimates/${rejectingEstimate.id}/status`, {
        action: 'reject',
        rejection_reason: rejectionReason
      });
      
      if (response.data.success) {
        setSuccess('Estimate rejected successfully');
        setShowRejectDialog(false);
        setRejectingEstimate(null);
        setRejectionReason('');
        loadEstimates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reject estimate');
    } finally {
      setSubmitting(false);
    }
  };

  const getBillingTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      page_based: 'Page Based',
      time_based: 'Time Based',
      case_based: 'Case Based',
    };
    return labels[type] || type;
  };

  const handleViewCase = (caseId: number) => {
    navigate(`/cases/${caseId}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Pending Client Estimates
      </Typography>

      <Paper elevation={2} sx={{ mt: 3 }}>
        {estimates.length === 0 ? (
          <Box p={4}>
            <Typography color="text.secondary" align="center">
              No pending estimates require your approval.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Case</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Client Firm</TableCell>
                  <TableCell>Billing Type</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {estimates.map((estimate) => (
                  <TableRow key={estimate.id}>
                    <TableCell>
                      <Box>
                        <Typography 
                          variant="body2" 
                          fontWeight="bold"
                          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => handleViewCase(estimate.case.id)}
                        >
                          {estimate.case.title}
                        </Typography>
                        {estimate.case.subtitle && (
                          <Typography variant="caption" color="text.secondary">
                            {estimate.case.subtitle}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{estimate.case.project.name}</TableCell>
                    <TableCell>{estimate.client_firm?.name || 'N/A'}</TableCell>
                    <TableCell>{getBillingTypeLabel(estimate.billing_type)}</TableCell>
                    <TableCell>
                      {estimate.quantity}
                      {(estimate.appendix_c_quantity ?? 0) > 0 && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Appendix C: {estimate.appendix_c_quantity}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      ${Number(estimate.rate).toFixed(2)}
                      {(estimate.appendix_c_quantity ?? 0) > 0 && estimate.appendix_c_rate != null && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Appendix C: ${Number(estimate.appendix_c_rate).toFixed(2)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">
                        ${Number(estimate.total_amount).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>{estimate.created_by_user?.name || 'N/A'}</TableCell>
                    <TableCell>
                      {new Date(estimate.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() => handleApprove(estimate.id)}
                          disabled={submitting}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          onClick={() => handleRejectClick(estimate)}
                          disabled={submitting}
                        >
                          Reject
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Reject Dialog */}
      <Dialog 
        open={showRejectDialog} 
        onClose={() => !submitting && setShowRejectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Estimate</DialogTitle>
        <DialogContent>
          {rejectingEstimate && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Case: <strong>{rejectingEstimate.case.title}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Amount: <strong>${Number(rejectingEstimate.total_amount).toFixed(2)}</strong>
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="Rejection Reason"
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            margin="normal"
            required
            helperText="Please provide a clear reason for rejecting this estimate"
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRejectDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleRejectConfirm} 
            variant="contained" 
            color="error"
            disabled={submitting || !rejectionReason.trim()}
          >
            {submitting ? <CircularProgress size={24} /> : 'Reject Estimate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PendingClientEstimates;

